import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { SEO, getServiceSchema, getFAQSchema } from '../../components/SEO';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../../components/ui/accordion";
import { Heart, CheckCircle, ArrowRight, Phone, Users, Home } from 'lucide-react';

export const FamilyTherapyPage = () => {
  const navigate = useNavigate();

  const faqs = [
    {
      question: "How long does family therapy take to see results?",
      answer: "Many families notice improvements within the first few sessions, particularly in communication and understanding. However, lasting change typically takes 8-12 sessions or more, depending on the complexity of issues. We'll set goals together and track progress along the way."
    },
    {
      question: "Do all family members need to attend every session?",
      answer: "While it's beneficial for everyone to participate, we can be flexible based on your family's needs and schedules. Sometimes I work with parents alone, sometimes with specific family subsystems (like siblings), and other times with the whole family. We'll determine the best approach together."
    },
    {
      question: "What if my family member doesn't want to come to therapy?",
      answer: "This is a common concern. I can work with whoever is willing to participate, and often as some family members experience positive changes, others become more open to joining. We can also discuss strategies to help reluctant family members feel more comfortable with the process."
    },
    {
      question: "Is family therapy available via telehealth in Hawaii?",
      answer: "Yes! Telehealth family therapy works very well and is available throughout Hawaii. Families can join from home, which can actually make everyone more comfortable. It's especially convenient for families with busy schedules or those living outside the Wailuku area."
    },
    {
      question: "Will you take sides or blame specific family members?",
      answer: "Absolutely not. Family therapy is not about blame—it's about understanding patterns and improving relationships. I work to create a safe, neutral space where everyone's perspective is heard and valued. My goal is to help the entire family system function better together."
    },
    {
      question: "Can family therapy help with divorce or separation?",
      answer: "Yes. Family therapy can help families navigate divorce or separation more healthily, particularly in developing effective co-parenting strategies and supporting children through the transition. The focus is on maintaining healthy relationships and minimizing negative impact on children."
    }
  ];

  const serviceSchema = getServiceSchema(
    "Family Therapy",
    "Professional family therapy services in Wailuku, Hawaii. Licensed therapist helping families improve communication, resolve conflicts, and strengthen bonds.",
    "https://aloha-parents.preview.emergentagent.com/services/family-therapy"
  );

  const faqSchema = getFAQSchema(faqs);

  const combinedSchema = {
    "@context": "https://schema.org",
    "@graph": [serviceSchema, faqSchema]
  };

  const benefits = [
    "Improve communication between family members",
    "Resolve conflicts and reduce family tension",
    "Strengthen family bonds and relationships",
    "Develop healthy coping strategies together",
    "Create a supportive home environment",
    "Navigate major life transitions as a family unit"
  ];

  const commonIssues = [
    "Family conflict and frequent arguments",
    "Communication breakdowns",
    "Divorce or separation adjustments",
    "Blended family challenges",
    "Parenting disagreements",
    "Sibling rivalry and conflict",
    "Major life transitions (moves, job changes)",
    "Loss and grief affecting the family",
    "Behavioral issues impacting family dynamics"
  ];

  return (
    <div className="min-h-screen bg-white pt-24 pb-20">
      <SEO 
        title="Family Therapy in Wailuku, HI"
        description="Expert family therapy services in Hawaii. Licensed therapist helping families improve communication, resolve conflicts, and strengthen relationships. Telehealth available."
        keywords="family therapy Hawaii, family counseling Wailuku, family therapist Maui, family conflict resolution Hawaii"
        canonicalUrl="https://aloha-parents.preview.emergentagent.com/services/family-therapy"
        schemaData={combinedSchema}
      />
      
      {/* Breadcrumb */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-8">
        <div className="flex items-center text-sm text-gray-600">
          <button onClick={() => navigate('/')} className="hover:text-teal-600 transition-colors">
            Home
          </button>
          <ArrowRight className="w-4 h-4 mx-2" />
          <button onClick={() => navigate('/#services')} className="hover:text-teal-600 transition-colors">
            Services
          </button>
          <ArrowRight className="w-4 h-4 mx-2" />
          <span className="text-gray-900 font-medium">Family Therapy</span>
        </div>
      </div>

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-pink-50 via-purple-50 to-white py-16 px-4 sm:px-6 lg:px-8 mb-16">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="w-16 h-16 bg-pink-100 rounded-2xl flex items-center justify-center mb-6">
                <Heart className="w-8 h-8 text-pink-600" />
              </div>
              <Badge className="mb-4 bg-pink-100 text-pink-800">Professional Family Therapy</Badge>
              <h1 className="text-5xl font-bold text-gray-900 mb-6">
                Family Therapy in Wailuku, HI
              </h1>
              <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                Families are complex systems where every member plays an important role. When challenges 
                arise, family therapy provides a safe space to improve communication, resolve conflicts, 
                and strengthen bonds that hold your family together.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button 
                  size="lg"
                  onClick={() => navigate('/contact')}
                  className="bg-pink-600 hover:bg-pink-700 text-white px-8 py-6 text-lg transition-all duration-200"
                >
                  <Phone className="w-5 h-5 mr-2" />
                  Book Family Session
                </Button>
                <Button 
                  size="lg"
                  variant="outline"
                  onClick={() => window.location.href = 'tel:8084707650'}
                  className="border-2 border-pink-600 text-pink-600 hover:bg-pink-50 px-8 py-6 text-lg transition-all duration-200"
                >
                  Call (808) 470-7650
                </Button>
              </div>
            </div>
            <div className="relative">
              <img 
                src="https://images.unsplash.com/photo-1511895426328-dc8714191300?w=800&q=80" 
                alt="Family together"
                className="rounded-2xl shadow-2xl w-full"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Overview */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              Strengthen Your Family Bond Through Collaborative Therapy
            </h2>
            <div className="prose prose-lg max-w-none text-gray-700 space-y-4">
              <p>
                Family therapy is a form of counseling that helps family members improve communication 
                and resolve conflicts. As a Licensed Mental Health Counselor with extensive experience 
                in family dynamics, I work with families of all types and sizes to address issues that 
                affect the family unit as a whole.
              </p>
              <p>
                Unlike individual therapy, family therapy focuses on the relationships and interactions 
                between family members. The goal is not to blame anyone but to understand patterns, 
                improve communication, and develop strategies that work for everyone. Each family member's 
                perspective is valued and heard in a safe, supportive environment.
              </p>
              <p>
                Whether you're dealing with everyday stressors or major life changes, family therapy can 
                help you navigate challenges together and emerge stronger. My approach is collaborative, 
                strengths-based, and tailored to your family's unique needs and cultural background.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Common Issues */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <Badge className="mb-4 bg-blue-100 text-blue-800">Common Challenges</Badge>
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Issues We Address in Family Therapy
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Family therapy can help with a wide range of challenges affecting your family
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {commonIssues.map((issue, index) => (
              <Card key={index} className="border-none shadow-lg bg-white hover:shadow-xl transition-all duration-300">
                <CardContent className="pt-6">
                  <div className="flex items-start">
                    <CheckCircle className="w-6 h-6 text-pink-600 mr-3 mt-1 flex-shrink-0" />
                    <p className="text-gray-700 leading-relaxed">{issue}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <Badge className="mb-4 bg-purple-100 text-purple-800">The Process</Badge>
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              How Family Therapy Works
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <Card className="border-none shadow-lg bg-gradient-to-br from-white to-pink-50">
              <CardHeader>
                <div className="w-12 h-12 bg-pink-600 text-white rounded-xl flex items-center justify-center mb-4 font-bold text-xl">
                  1
                </div>
                <CardTitle className="text-2xl">Initial Family Assessment</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-700 leading-relaxed">
                  We'll meet together to discuss family dynamics, identify challenges, and set goals. 
                  Everyone's perspective is important and valued.
                </p>
              </CardContent>
            </Card>

            <Card className="border-none shadow-lg bg-gradient-to-br from-white to-purple-50">
              <CardHeader>
                <div className="w-12 h-12 bg-purple-600 text-white rounded-xl flex items-center justify-center mb-4 font-bold text-xl">
                  2
                </div>
                <CardTitle className="text-2xl">Collaborative Sessions</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-700 leading-relaxed">
                  Through guided discussions and activities, we'll work on communication skills, 
                  conflict resolution, and understanding each other better.
                </p>
              </CardContent>
            </Card>

            <Card className="border-none shadow-lg bg-gradient-to-br from-white to-blue-50">
              <CardHeader>
                <div className="w-12 h-12 bg-blue-600 text-white rounded-xl flex items-center justify-center mb-4 font-bold text-xl">
                  3
                </div>
                <CardTitle className="text-2xl">Implementation & Growth</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-700 leading-relaxed">
                  Apply new strategies at home, track progress, and continue building stronger 
                  relationships with ongoing support and guidance.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="relative">
              <img 
                src="https://images.unsplash.com/photo-1609220136736-443140cffec6?w=800&q=80" 
                alt="Happy family"
                className="rounded-2xl shadow-xl w-full"
              />
            </div>
            <div>
              <Badge className="mb-4 bg-green-100 text-green-800">Benefits</Badge>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                The Benefits of Family Therapy
              </h2>
              <p className="text-lg text-gray-600 mb-8">
                Family therapy can transform your family dynamics and create lasting positive change:
              </p>
              <div className="space-y-4">
                {benefits.map((benefit, index) => (
                  <div key={index} className="flex items-start">
                    <CheckCircle className="w-6 h-6 text-pink-600 mr-4 mt-1 flex-shrink-0" />
                    <p className="text-gray-700 text-lg leading-relaxed">{benefit}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Family Types */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto text-center">
          <Badge className="mb-4 bg-indigo-100 text-indigo-800">All Families Welcome</Badge>
          <h2 className="text-4xl font-bold text-gray-900 mb-6">
            We Work With All Types of Families
          </h2>
          <p className="text-xl text-gray-600 mb-12 max-w-3xl mx-auto">
            Every family is unique, and I welcome families of all structures and backgrounds
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <Card className="border-none shadow-lg hover:shadow-xl transition-all duration-300">
              <CardContent className="pt-8 pb-8 text-center">
                <Users className="w-12 h-12 text-pink-600 mx-auto mb-4" />
                <h3 className="font-bold text-xl text-gray-900 mb-2">Nuclear Families</h3>
                <p className="text-gray-600">Traditional family structures</p>
              </CardContent>
            </Card>

            <Card className="border-none shadow-lg hover:shadow-xl transition-all duration-300">
              <CardContent className="pt-8 pb-8 text-center">
                <Heart className="w-12 h-12 text-purple-600 mx-auto mb-4" />
                <h3 className="font-bold text-xl text-gray-900 mb-2">Blended Families</h3>
                <p className="text-gray-600">Step-families and co-parenting</p>
              </CardContent>
            </Card>

            <Card className="border-none shadow-lg hover:shadow-xl transition-all duration-300">
              <CardContent className="pt-8 pb-8 text-center">
                <Home className="w-12 h-12 text-blue-600 mx-auto mb-4" />
                <h3 className="font-bold text-xl text-gray-900 mb-2">Extended Families</h3>
                <p className="text-gray-600">Multi-generational households</p>
              </CardContent>
            </Card>

            <Card className="border-none shadow-lg hover:shadow-xl transition-all duration-300">
              <CardContent className="pt-8 pb-8 text-center">
                <Users className="w-12 h-12 text-teal-600 mx-auto mb-4" />
                <h3 className="font-bold text-xl text-gray-900 mb-2">Single Parent</h3>
                <p className="text-gray-600">Solo parenting situations</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <Badge className="mb-4 bg-amber-100 text-amber-800">Common Questions</Badge>
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Family Therapy Questions Answered
            </h2>
          </div>

          <Accordion type="single" collapsible className="space-y-4">
            <AccordionItem 
              value="item-1"
              className="bg-gray-50 border-none shadow-md rounded-lg px-6"
            >
              <AccordionTrigger className="text-left text-lg font-semibold text-gray-900 hover:text-pink-600 transition-colors">
                How long does family therapy take to see results?
              </AccordionTrigger>
              <AccordionContent className="text-gray-600 text-base leading-relaxed pt-2">
                Many families notice improvements within the first few sessions, particularly in communication 
                and understanding. However, lasting change typically takes 8-12 sessions or more, depending on 
                the complexity of issues. We'll set goals together and track progress along the way.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem 
              value="item-2"
              className="bg-gray-50 border-none shadow-md rounded-lg px-6"
            >
              <AccordionTrigger className="text-left text-lg font-semibold text-gray-900 hover:text-pink-600 transition-colors">
                Do all family members need to attend every session?
              </AccordionTrigger>
              <AccordionContent className="text-gray-600 text-base leading-relaxed pt-2">
                While it's beneficial for everyone to participate, we can be flexible based on your family's needs 
                and schedules. Sometimes I work with parents alone, sometimes with specific family subsystems (like 
                siblings), and other times with the whole family. We'll determine the best approach together.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem 
              value="item-3"
              className="bg-gray-50 border-none shadow-md rounded-lg px-6"
            >
              <AccordionTrigger className="text-left text-lg font-semibold text-gray-900 hover:text-pink-600 transition-colors">
                What if my family member doesn't want to come to therapy?
              </AccordionTrigger>
              <AccordionContent className="text-gray-600 text-base leading-relaxed pt-2">
                This is a common concern. I can work with whoever is willing to participate, and often as some 
                family members experience positive changes, others become more open to joining. We can also discuss 
                strategies to help reluctant family members feel more comfortable with the process.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem 
              value="item-4"
              className="bg-gray-50 border-none shadow-md rounded-lg px-6"
            >
              <AccordionTrigger className="text-left text-lg font-semibold text-gray-900 hover:text-pink-600 transition-colors">
                Is family therapy available via telehealth in Hawaii?
              </AccordionTrigger>
              <AccordionContent className="text-gray-600 text-base leading-relaxed pt-2">
                Yes! Telehealth family therapy works very well and is available throughout Hawaii. Families can 
                join from home, which can actually make everyone more comfortable. It's especially convenient for 
                families with busy schedules or those living outside the Wailuku area.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem 
              value="item-5"
              className="bg-gray-50 border-none shadow-md rounded-lg px-6"
            >
              <AccordionTrigger className="text-left text-lg font-semibold text-gray-900 hover:text-pink-600 transition-colors">
                Will you take sides or blame specific family members?
              </AccordionTrigger>
              <AccordionContent className="text-gray-600 text-base leading-relaxed pt-2">
                Absolutely not. Family therapy is not about blame—it's about understanding patterns and improving 
                relationships. I work to create a safe, neutral space where everyone's perspective is heard and 
                valued. My goal is to help the entire family system function better together.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem 
              value="item-6"
              className="bg-gray-50 border-none shadow-md rounded-lg px-6"
            >
              <AccordionTrigger className="text-left text-lg font-semibold text-gray-900 hover:text-pink-600 transition-colors">
                Can family therapy help with divorce or separation?
              </AccordionTrigger>
              <AccordionContent className="text-gray-600 text-base leading-relaxed pt-2">
                Yes. Family therapy can help families navigate divorce or separation more healthily, particularly 
                in developing effective co-parenting strategies and supporting children through the transition. 
                The focus is on maintaining healthy relationships and minimizing negative impact on children.
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-pink-600 to-purple-600 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Ready to Strengthen Your Family Bond?
          </h2>
          <p className="text-xl mb-8 opacity-95">
            Start your family's healing journey today with compassionate, expert guidance.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              size="lg"
              onClick={() => navigate('/contact')}
              className="bg-white text-pink-600 hover:bg-gray-100 px-8 py-6 text-lg font-semibold transition-all duration-200 shadow-xl"
            >
              Schedule Family Session
            </Button>
            <Button 
              size="lg"
              variant="outline"
              onClick={() => navigate('/resources')}
              className="border-2 border-white text-white hover:bg-white/10 px-8 py-6 text-lg font-semibold transition-all duration-200"
            >
              Read Family Resources
            </Button>
          </div>
          <p className="mt-6 text-sm opacity-80">
            Telehealth and in-person sessions available • All family types welcome
          </p>
        </div>
      </section>
    </div>
  );
};
